import React, { Suspense, lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Select } from '@components';
import {
  getScenarioTestCallerNumber,
  getScenarioTestNow,
  setScenarioTestCallerNumber,
  setScenarioTestNow,
} from '@utils/scenarioTestCaller';
import { useScenarioCredentials } from '../../hooks/useScenario';
import type { ScenarioDraftController, ScenarioEditorMode } from '../../hooks/useScenarioDraft';
import type { ScenarioConfig } from '../scenario/types';
import { ScenarioFormEditor } from '../scenario/ScenarioFormEditor';
import { ScenarioJsonEditor } from '../scenario/ScenarioJsonEditor';
import { CredentialsManager } from '../scenario/CredentialsManager';
import { ToolTestPanel } from '../scenario/ToolTestPanel';
import { Section, TextField, Toggle } from '../scenario/ui';

// React Flow and dagre are only loaded when the flow view is opened.
const ScenarioFlowView = lazy(() => import('../scenario/flow/ScenarioFlowView'));

const MODES: { mode: ScenarioEditorMode; label: string }[] = [
  { mode: 'form', label: 'agents.scenario.modeForm' },
  { mode: 'json', label: 'agents.scenario.modeJson' },
  { mode: 'flow', label: 'agents.scenario.modeFlow' },
];

const Spinner: React.FC = () => (
  <div className="flex items-center justify-center py-12">
    <span className="material-icons animate-spin text-primary-500">progress_activity</span>
  </div>
);

interface ScenarioSubTabProps {
  agentId?: string;
  scenario: ScenarioDraftController;
  enabledSystemTools?: string[];
}

const STATUS_STYLES = {
  success: {
    className: 'border-success-200 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-900/20 dark:text-success-300',
    icon: 'check_circle',
  },
  error: {
    className: 'border-error-200 bg-error-50 text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300',
    icon: 'error',
  },
  info: {
    className: 'border-info-200 bg-info-50 text-info-700 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300',
    icon: 'info',
  },
};

export const ScenarioSubTab: React.FC<ScenarioSubTabProps> = ({
  agentId,
  scenario: controller,
  enabledSystemTools,
}) => {
  const { t } = useTranslation();
  const {
    scenario,
    meta,
    loading,
    loadError,
    testTool,
    mode,
    draft,
    text,
    parseError,
    currentConfig,
    enabled,
    dirty,
    issues,
    warnings,
    preview,
    status,
    modeError,
  } = controller;
  const credentialsApi = useScenarioCredentials(Boolean(agentId));
  const [callerNumber, setCallerNumber] = useState(() => (agentId ? getScenarioTestCallerNumber(agentId) : ''));
  const [testNow, setTestNow] = useState(() => (agentId ? getScenarioTestNow(agentId) : ''));
  // Tool the form opens and scrolls to after "open in form" in the flow view.
  const [focusTool, setFocusTool] = useState<number | null>(null);

  if (!agentId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <span className="material-icons text-gray-400 dark:text-gray-500 text-5xl mb-3">save</span>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">{t('agents.scenario.saveAgentFirst')}</p>
      </div>
    );
  }

  if (loading && !scenario) {
    return <Spinner />;
  }

  if (loadError && !scenario) {
    return <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>;
  }

  const exampleOptions = Object.entries(meta?.examples ?? {}).map(([key, example]) => ({
    value: key,
    label: example.name,
  }));

  return (
    <div className="space-y-4" data-testid="scenario-sub-tab">
      <p className="text-sm text-gray-600 dark:text-gray-400">{t('agents.scenario.description')}</p>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <Toggle
          label={t('agents.scenario.enabled')}
          checked={enabled}
          onChange={controller.setEnabled}
        />
        <Badge variant="outline" size="sm">
          {scenario?.active_version
            ? t('agents.scenario.activeVersion', { number: scenario.active_version.number })
            : t('agents.scenario.noVersion')}
        </Badge>
        {dirty && (
          <Badge variant="warning" size="sm">
            {t('agents.scenario.unsaved')}
          </Badge>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600" role="group">
            {MODES.map((item, index) => (
              <button
                key={item.mode}
                type="button"
                aria-pressed={mode === item.mode}
                className={`px-3 py-1 text-sm ${
                  mode === item.mode
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                } ${index === 0 ? 'rounded-l-lg' : ''} ${index === MODES.length - 1 ? 'rounded-r-lg' : ''}`}
                onClick={() => {
                  setFocusTool(null);
                  controller.switchMode(item.mode);
                }}
              >
                {t(item.label)}
              </button>
            ))}
          </div>
          {exampleOptions.length > 0 && (
            <div className="w-56">
              <Select
                size="sm"
                value=""
                placeholder={t('agents.scenario.insertExample')}
                options={exampleOptions}
                onChange={value => controller.loadExample(Array.isArray(value) ? value[0] : value)}
              />
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={controller.validateNow}
            disabled={currentConfig === null}
          >
            {t('agents.scenario.validate')}
          </Button>
          <Button variant="ghost" size="sm" onClick={controller.discard} disabled={!dirty}>
            {t('agents.scenario.discard')}
          </Button>
        </div>
      </div>

      {status && (
        <div className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${STATUS_STYLES[status.type].className}`}>
          <span className="material-icons text-lg">{STATUS_STYLES[status.type].icon}</span>
          <span>{status.text}</span>
        </div>
      )}
      {modeError && <p className="text-sm text-error-600 dark:text-error-400">{modeError}</p>}
      {issues.length > 0 && (
        <p className="text-sm text-error-600 dark:text-error-400">
          {t('agents.scenario.issuesCount', { count: issues.length })}
        </p>
      )}

      {mode === 'form' && draft ? (
        <ScenarioFormEditor
          config={draft}
          onChange={controller.changeDraft}
          issues={issues}
          credentials={credentialsApi.credentials}
          systemTools={meta?.system_tools ?? []}
          enabledSystemTools={enabledSystemTools}
          initialOpenTool={focusTool}
        />
      ) : mode === 'flow' && draft ? (
        <Suspense fallback={<Spinner />}>
          <ScenarioFlowView
            config={draft}
            issues={issues}
            onOpenTool={index => {
              setFocusTool(index);
              controller.switchMode('form');
            }}
          />
        </Suspense>
      ) : (
        <ScenarioJsonEditor
          text={text}
          onChange={controller.changeText}
          issues={issues}
          parseError={parseError}
        />
      )}

      {warnings.length > 0 && (
        <ul className="space-y-1 rounded-lg border border-warning-200 bg-warning-50 p-3 dark:border-warning-800 dark:bg-warning-900/20">
          {warnings.map((warning, index) => (
            <li key={index} className="text-xs text-warning-700 dark:text-warning-300">
              {warning.message} <span className="font-mono text-gray-500 dark:text-gray-400">{warning.pointer}</span>
            </li>
          ))}
        </ul>
      )}

      <Section title={t('agents.scenario.credentials')} icon="key" defaultOpen={false}>
        <CredentialsManager
          credentials={credentialsApi.credentials}
          error={credentialsApi.error}
          onSave={credentialsApi.upsert}
          onRemove={credentialsApi.remove}
        />
      </Section>

      <Section title={t('agents.scenario.test')} icon="science" defaultOpen={false}>
        <div className="grid max-w-2xl grid-cols-1 gap-3 md:grid-cols-2">
          <TextField
            label={t('agents.scenario.testCallerNumber')}
            hint={t('agents.scenario.testCallerNumberHint')}
            value={callerNumber}
            onChange={value => {
              setCallerNumber(value);
              setScenarioTestCallerNumber(agentId, value.trim());
            }}
            issues={[]}
            pointer="/test/caller_number"
            placeholder="+491701234567"
          />
          <TextField
            label={t('agents.scenario.testNow')}
            hint={t('agents.scenario.testNowHint')}
            type="datetime-local"
            value={testNow}
            onChange={value => {
              setTestNow(value);
              setScenarioTestNow(agentId, value);
            }}
            issues={[]}
            pointer="/test/now"
          />
        </div>
        <ToolTestPanel
          config={(currentConfig as ScenarioConfig | null) ?? null}
          callerNumber={callerNumber.trim()}
          now={testNow}
          onRun={testTool}
        />
      </Section>

      <Section title={t('agents.scenario.preview')} icon="visibility" defaultOpen={false}>
        {preview ? (
          <div className="space-y-3">
            <div>
              <p className="mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('agents.scenario.previewPrompt')}
              </p>
              <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-gray-100 p-2 text-xs dark:bg-gray-800 dark:text-gray-100">
                {preview.prompt_section}
              </pre>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('agents.scenario.previewDeclarations')}
              </p>
              <ul className="space-y-2">
                {preview.declarations.map(declaration => (
                  <li key={declaration.name} className="text-xs">
                    <span className="font-mono font-semibold">{declaration.name}</span>
                    <pre className="whitespace-pre-wrap text-gray-600 dark:text-gray-400">{declaration.description}</pre>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('agents.scenario.previewEmpty')}</p>
        )}
      </Section>
    </div>
  );
};
